/**
 * AutoDisablePanel — configurable failure-based channel auto-disable.
 *
 * Props:
 *   scope: "admin"    → reads/writes /api/admin/auto-disable-config
 *          "partner"  → reads/writes /api/partner/auto-disable-config
 *   showClearOverride: bool (partner-only) — shows "Reset to admin default"
 */
import { useEffect, useState } from "react";
import { Loader2, Save, ShieldAlert, Info } from "lucide-react";
import api, { formatDetail } from "@/lib/api";

const FAIL_TYPES = [
    { key: "partner_rejected", label: "Partner rejected", hint: "Merchant clicked Reject on the approval." },
    { key: "auto_expired", label: "Auto-expired", hint: "Customer never completed within the 24h window." },
    { key: "customer_cancelled", label: "Customer cancelled", hint: "Customer closed the payment page." },
];
const WINDOWS = [
    { key: "consecutive", label: "Consecutive", hint: "In-a-row. One success resets to 0." },
    { key: "rolling", label: "Rolling window", hint: "N failures in the last M minutes." },
    { key: "daily", label: "Daily", hint: "N failures in the same day (resets at midnight IST)." },
];
const PRIORITIES = [
    { key: "default", label: "Default (round-robin)" },
    { key: "prefer_bank", label: "Prefer bank over UPI" },
    { key: "lowest_fail_count", label: "Prefer channel with fewest fails" },
];

export default function AutoDisablePanel({ scope, showClearOverride = false }) {
    const endpoint = scope === "admin" ? "/admin/auto-disable-config" : "/partner/auto-disable-config";
    const [cfg, setCfg] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    const reload = async () => {
        setBusy(true);
        try {
            const r = await api.get(endpoint);
            setCfg(r.data);
            setDraft(r.data);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };
    useEffect(() => { reload();   }, [scope]);

    if (!draft) return <div className="text-xs text-slate-400">Loading auto-disable config…</div>;

    const toggleFailType = (t) => {
        const cur = new Set(draft.fail_types || []);
        if (cur.has(t)) cur.delete(t); else cur.add(t);
        setDraft({ ...draft, fail_types: Array.from(cur).sort() });
    };

    const save = async () => {
        setBusy(true); setErr(""); setMsg("");
        try {
            const body = {
                enabled: !!draft.enabled,
                fail_types: draft.fail_types || [],
                threshold: draft.threshold,
                window_type: draft.window_type,
                window_minutes: draft.window_minutes,
                replacement_priority: draft.replacement_priority,
            };
            const r = await api.patch(endpoint, body);
            setCfg(r.data); setDraft(r.data);
            setMsg("Saved.");
            setTimeout(() => setMsg(""), 1500);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const clearOverride = async () => {
        if (!window.confirm("Reset to admin-wide default? Your partner-specific settings will be discarded.")) return;
        setBusy(true); setErr("");
        try {
            const r = await api.delete(endpoint);
            setCfg(r.data); setDraft(r.data);
            setMsg("Override cleared — inheriting admin default.");
            setTimeout(() => setMsg(""), 2500);
        } catch (e) {
            setErr(formatDetail(e?.response?.data?.detail) || e.message);
        } finally { setBusy(false); }
    };

    const inheriting = cfg?._source === "admin_default";

    return (
        <div className="card p-5 space-y-4 text-sm" data-testid={`autodisable-panel-${scope}`}>
            <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="flex items-start gap-2">
                    <ShieldAlert className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div>
                        <div className="font-semibold">Auto-disable channels on repeated failures</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">
                            When a bank / UPI channel accumulates enough failures, it&apos;s turned off automatically
                            and routing shifts to the next eligible channel.
                            {scope === "partner" && inheriting && (
                                <span className="ml-1 inline-flex items-center gap-1 text-blue-600">
                                    <Info className="w-3 h-3" /> Inheriting admin-wide default.
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                    <input data-testid="ad-enabled" type="checkbox" checked={!!draft.enabled}
                           onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} />
                    <span className="text-xs font-medium">{draft.enabled ? "Enabled" : "Disabled"}</span>
                </label>
            </div>

            {/* Fail types (checkbox group) */}
            <div className={draft.enabled ? "" : "opacity-50 pointer-events-none"}>
                <label className="label">Which failures count?</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {FAIL_TYPES.map((ft) => {
                        const on = (draft.fail_types || []).includes(ft.key);
                        return (
                            <label key={ft.key} data-testid={`ad-fail-${ft.key}`}
                                   className={`border rounded-md p-2 cursor-pointer transition-colors ${on ? "border-amber-400 bg-amber-50" : "border-slate-200 bg-white"}`}>
                                <div className="flex items-center gap-2">
                                    <input type="checkbox" checked={on} onChange={() => toggleFailType(ft.key)} />
                                    <span className="font-medium text-xs">{ft.label}</span>
                                </div>
                                <div className="text-[10px] text-slate-500 mt-1">{ft.hint}</div>
                            </label>
                        );
                    })}
                </div>
            </div>

            {/* Threshold + window */}
            <div className={`grid grid-cols-1 sm:grid-cols-3 gap-3 ${draft.enabled ? "" : "opacity-50 pointer-events-none"}`}>
                <div>
                    <label className="label">Threshold (failures)</label>
                    <input data-testid="ad-threshold" type="number" min={1} max={100}
                           value={draft.threshold} className="input"
                           onChange={(e) => setDraft({ ...draft, threshold: Math.max(1, Math.min(100, parseInt(e.target.value || "1", 10))) })} />
                </div>
                <div>
                    <label className="label">Window</label>
                    <select data-testid="ad-window" value={draft.window_type} className="input"
                            onChange={(e) => setDraft({ ...draft, window_type: e.target.value })}>
                        {WINDOWS.map((w) => <option key={w.key} value={w.key}>{w.label}</option>)}
                    </select>
                    <div className="text-[10px] text-slate-500 mt-1">
                        {(WINDOWS.find((w) => w.key === draft.window_type) || {}).hint}
                    </div>
                </div>
                {draft.window_type === "rolling" && (
                    <div>
                        <label className="label">Window (minutes)</label>
                        <input data-testid="ad-window-min" type="number" min={1} max={1440}
                               value={draft.window_minutes} className="input"
                               onChange={(e) => setDraft({ ...draft, window_minutes: parseInt(e.target.value || "60", 10) })} />
                    </div>
                )}
            </div>

            {/* Replacement priority */}
            <div className={draft.enabled ? "" : "opacity-50 pointer-events-none"}>
                <label className="label">Replacement priority</label>
                <select data-testid="ad-replacement" value={draft.replacement_priority} className="input"
                        onChange={(e) => setDraft({ ...draft, replacement_priority: e.target.value })}>
                    {PRIORITIES.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
                </select>
                <div className="text-[10px] text-slate-500 mt-1">
                    Applied every time the routing engine picks a channel. &ldquo;Prefer channel with fewest fails&rdquo; keeps
                    healthier channels in rotation.
                </div>
            </div>

            {err && <div className="text-rose-600 text-xs" data-testid="ad-err">{err}</div>}
            {msg && <div className="text-emerald-600 text-xs" data-testid="ad-msg">{msg}</div>}

            <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
                {showClearOverride && scope === "partner" && !inheriting && (
                    <button type="button" onClick={clearOverride} disabled={busy}
                            className="btn-secondary px-3 py-2 rounded-md text-xs">
                        Reset to admin default
                    </button>
                )}
                <button data-testid="ad-save" type="button" onClick={save} disabled={busy}
                        className="btn-primary px-4 py-2 rounded-md text-sm flex items-center gap-2">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Save
                </button>
            </div>
        </div>
    );
}
